import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import SmsIcon from '@mui/icons-material/Sms';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { capitalize, startCase } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { FormStateChangeProps } from '~formsShared/types';
import { AdminCompany } from '~hooks/admin/useCompaniesAdmin/models';
import useCompaniesAdmin from '~hooks/admin/useCompaniesAdmin/useCompaniesAdmin';
import { CompanyBasic } from '~hooks/useCompany/models';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { useInlineUploadError } from '~hooks/useInlineUploadError/useInlineUploadError';
import { ADMIN_ACTIONS_COLUMN_FIELD } from '~pages/Admin/adminActionsColumnField';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';

import { DatadogLink } from '../External/DatadogLink';
import { SentryLink } from '../External/SentryLink';
import { CompanyForm, CompanyFormHandler, CompanyFormValues } from './CompanyForm';
import { SetUpTicketChasingDialog } from './SetUpTicketChasingDialog';

const AdminCompaniesDataGrid = () => {
  const { toasterStore } = useStores();
  const { getAllCompanies, postCompanyUpdate, deleteCompany, uploadLogo } =
    useCompaniesAdmin();
  const { setSearchQueryValue, searchValue } = useDataGridSearch();
  const {
    goToPage: goToCompaniesPage,
    isFetching: isFetchingCompanies,
    pagination,
    reloadCurrentPage: reloadCompaniesPage,
    rows,
    startOver: startCompaniesOver,
  } = useCursorPagination<AdminCompany>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      getAllCompanies({ cursor, limit, link, searchQuery: searchValue, signal }),
    limit: MAX_API_PAGE_SIZE,
  });
  const [isCompanyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [isDeleteCompanyDialogOpen, setIsDeleteCompanyDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const updateDialogRef = useRef<CompanyFormHandler>(null);
  const [logo, setLogo] = useState<Nullable<File>>(null);
  const {
    uploadError: logoError,
    clearUploadError: clearLogoError,
    showUploadError: showLogoError,
  } = useInlineUploadError();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ticketChasingCompany, setTicketChasingCompany] = useState<{
    id: string;
    legalName: string;
  } | null>(null);
  const [isTicketChasingBusy, setIsTicketChasingBusy] = useState(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const closeCompanyDialog = () => {
    setCompanyDialogOpen(false);

    // Discard any staged-but-unsubmitted logo file. `logo` lives here (not in
    // CompanyForm/ImageUpload) so it survives the dialog's own unmount, and
    // without this it would silently re-upload on the next submit for this
    // company — even if the user never re-chose a file in the reopened dialog.
    setLogo(null);
    clearLogoError();
  };

  const handleClose = () => {
    if (isUpdating) {
      return;
    }

    closeCompanyDialog();
  };

  const companyOptions = useMemo(
    () => rows.map((co) => ({ legalName: co.legalName, id: co.id })),
    [rows],
  );
  const selectedCompany = useMemo(() => {
    if (rows.length && selectedId) {
      return rows.find((company) => company.id === selectedId) || undefined;
    }
    return null;
  }, [selectedId, rows]);

  useEffect(() => {
    void startCompaniesOver();
  }, [searchValue, startCompaniesOver]);

  const closeDeleteCompanyDialog = () => {
    setIsDeleteCompanyDialogOpen(false);
  };

  const handleCloseDeleteCompanyDialog = () => {
    if (isUpdating) {
      return;
    }

    closeDeleteCompanyDialog();
  };

  const deleteRow = (id: string) => {
    setSelectedId(id);
    setIsDeleteCompanyDialogOpen(true);
  };

  const deleteCompanyCallback = async () => {
    if (!selectedId) {
      return;
    }

    setIsUpdating(true);

    try {
      await deleteCompany(selectedId);
      closeDeleteCompanyDialog();
      void reloadCompaniesPage();
      toasterStore.push(alert(t('company.company_deleted'), AlertTypes.success));
    } catch {
      // Error toast is handled in the hook.
    } finally {
      setIsUpdating(false);
    }
  };
  const onChangeQuickFilter = (searchQuery: string) => {
    setSearchQueryValue(searchQuery);
  };

  const editRow = (id: string) => {
    setSelectedId(id);
    setCompanyDialogOpen(true);
  };

  const createNew = () => {
    setSelectedId(null);
    setCompanyDialogOpen(true);
  };

  const onSuccess = (company?: AdminCompany) => {
    setSelectedId(null);

    closeCompanyDialog();
    void reloadCompaniesPage();
    toasterStore.push(
      alert(
        selectedCompany?.id
          ? t('company.company_updated', { name: company?.legalName })
          : t('company.company_created', { name: company?.legalName }),
        AlertTypes.success,
      ),
    );
  };

  const onChangeLogo = (file: Nullable<File>) => {
    setLogo(file);
    // Clear any error from a previous failed attempt now that the user has staged
    // a new file to try again.
    clearLogoError();
  };

  const uploadCompanyLogo = async (companyId: string, file: File) => {
    try {
      const companyWithLogo = await uploadLogo({ id: companyId, file });
      onSuccess(companyWithLogo);
    } catch (error) {
      void reloadCompaniesPage();
      showLogoError(error, t('error_messages.company.failed_to_upload_logo'));
    }
  };
  const onSubmitCallback = async (data: CompanyFormValues) => {
    setIsUpdating(true);

    try {
      const savedCompany = await postCompanyUpdate({
        ...data,
        id: data.id || selectedId || '',
      } as unknown as AdminCompany);
      setSelectedId(savedCompany.id);

      if (logo) {
        await uploadCompanyLogo(savedCompany.id, logo);
      } else {
        onSuccess(savedCompany);
      }
    } catch {
      // Error toast is handled in the hook.
    } finally {
      setIsUpdating(false);
    }
  };

  const onSubmitCompanyForm = () => {
    updateDialogRef.current?.submit(onSubmitCallback);
  };

  const onValidateCompanyForm = ({ isDirty }: FormStateChangeProps) => {
    setIsDirty(isDirty);
  };

  const columns = useMemo<Array<DataTableColumn<AdminCompany>>>(
    () => [
      {
        field: 'legalName',
        headerName: t('form_fields.company'),
        renderCell: (company) => (
          <>
            <Typography component="span" sx={{ mr: 1 }}>
              {company.logoUrl && (
                <img
                  src={company.logoPreviewUrl || company.logoUrl}
                  alt="logo"
                  width={25}
                />
              )}
            </Typography>
            <Tooltip title={company.dbaName}>
              <Typography component="span">{company.legalName}</Typography>
            </Tooltip>
          </>
        ),
      },
      {
        defaultHidden: true,
        field: 'dbaName',
        headerName: t('form_fields.dba'),
        valueGetter: (company) => company.dbaName,
      },
      {
        field: 'parentCompany',
        headerName: t('form_fields.parent_company'),
        valueGetter: (company) => company.parentCompany?.legalName || '',
      },
      {
        field: 'treadId',
        headerName: t('form_fields.tread_id'),
        valueGetter: (company) => company.treadId,
      },
      {
        field: 'id',
        headerName: t('form_fields.uuid'),
        renderCell: (company) => (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2">{company.id}</Typography>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
              <DatadogLink companyId={company.id} />
              <SentryLink companyId={company.id} />
            </div>
          </Box>
        ),
      },
      {
        field: 'companyType',
        headerName: t('form_fields.type'),
        valueGetter: (company) =>
          company.companyType ? startCase(company.companyType) : '',
      },
      {
        defaultHidden: true,
        field: 'address',
        headerName: t('form_fields.address'),
        renderCell: (company) => {
          if (!company.address) {
            return null;
          }

          const { streetAddress, premise, locality, country, administrativeArea } =
            company.address;
          const address = `${streetAddress}${premise ? ` ${premise},` : ','} ${locality}, ${administrativeArea}, ${country?.name ?? ''}`;

          return (
            <Tooltip title={address}>
              <Typography component="span" variant="body1" color="secondary">
                {address}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'timeZone',
        headerName: t('form_fields.time_zone'),
        valueGetter: (company) => company.timeZone,
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        valueGetter: (company) => dateFormat(company.createdAt),
      },
      {
        field: 'usageStatus',
        headerName: t('form_fields.usage_status'),
        renderCell: (company) => (
          <Typography component="span">
            {capitalize(company.usageStatus ?? '')}
          </Typography>
        ),
      },
      {
        field: 'billingStatus',
        headerName: t('form_fields.billing_status'),
        renderCell: (company) => (
          <Typography component="span">
            {capitalize(company.billingStatus ?? '')}
          </Typography>
        ),
      },
      {
        field: 'saasBillingPlan',
        headerName: t('form_fields.plan'),
        renderCell: (company) => (
          <Typography component="span">
            {capitalize(company.saasBillingPlan ?? '')}
          </Typography>
        ),
      },
      {
        defaultHidden: true,
        field: 'primaryContact',
        headerName: t('form_fields.primary_contact'),
        valueGetter: (company) => company.primaryContact?.name,
      },
      {
        defaultHidden: true,
        field: 'primaryPhone',
        headerName: t('form_fields.primary_phone'),
        valueGetter: (company) => company.primaryContact?.phone,
      },
      {
        defaultHidden: true,
        field: 'primaryEmail',
        headerName: t('form_fields.email'),
        valueGetter: (company) => company.primaryContact?.email,
      },
      {
        defaultHidden: true,
        field: 'billingContact',
        headerName: t('form_fields.billing_contact'),
        valueGetter: (company) => company.billingContact?.name,
      },
      {
        defaultHidden: true,
        field: 'billingPhone',
        headerName: t('form_fields.billing_phone'),
        valueGetter: (company) => company.billingContact?.phone,
      },
      {
        defaultHidden: true,
        field: 'billingEmail',
        headerName: t('form_fields.billing_email'),
        valueGetter: (company) => company.billingContact?.email,
      },
      actionsColumn(
        (company) => (
          <>
            <IconButton
              aria-label={t('actions.edit')}
              size="small"
              onClick={() => editRow(company.id)}
            >
              <Edit />
            </IconButton>
            <SimpleMenu
              options={[
                {
                  title: t('company.enable_ticket_chasing'),
                  icon: <SmsIcon />,
                  callBack: () =>
                    setTicketChasingCompany({
                      id: company.id,
                      legalName: company.legalName,
                    }),
                },
                {
                  title: t('actions.delete'),
                  icon: <Delete />,
                  callBack: () => deleteRow(company.id),
                },
              ]}
            >
              <MoreHoriz />
            </SimpleMenu>
          </>
        ),
        ADMIN_ACTIONS_COLUMN_FIELD,
      ),
    ],
    [],
  );

  return (
    <>
      <Box
        data-test-id="company-data-grid"
        sx={{
          display: 'block',
          mt: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            data-test-id={'create-company-btn'}
            color={'primary'}
            onClick={createNew}
            startIcon={<Add />}
          >
            {t('company.create_company')}
          </Button>
        </Box>
        <DataTable
          id="admin-company-grid"
          columns={columns}
          rows={rows}
          loading={isFetchingCompanies || isUpdating || isTicketChasingBusy}
          onSearchChange={onChangeQuickFilter}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetchingCompanies || isUpdating || isTicketChasingBusy}
              onNavigate={(link) => void goToCompaniesPage(link)}
              pagination={pagination}
            />
          }
        />
      </Box>
      <Dialog
        isOpen={isCompanyDialogOpen}
        onClose={handleClose}
        preventCloseOnBackdropClick
      >
        <DialogHeader onClose={handleClose}>
          {selectedCompany?.id
            ? t('company.update_company')
            : t('company.create_company')}
        </DialogHeader>

        <DialogContent>
          <CompanyForm
            ref={updateDialogRef}
            companyOptions={companyOptions as unknown as CompanyBasic[]}
            defaultCompany={selectedCompany}
            isAdminPage
            logoErrorMessage={logoError}
            onChangeLogo={onChangeLogo}
            onFormStateChange={onValidateCompanyForm}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isUpdating}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            data-test-id={'create-company-btn'}
            disabled={!isDirty || isUpdating}
            loading={isUpdating}
            onClick={onSubmitCompanyForm}
            variant="contained"
          >
            {selectedCompany?.id ? t('actions.submit') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <SetUpTicketChasingDialog
        company={ticketChasingCompany}
        onBusyChange={setIsTicketChasingBusy}
        onClose={() => setTicketChasingCompany(null)}
      />
      <ConfirmationDialog
        isBusy={isUpdating}
        isOpen={isDeleteCompanyDialogOpen}
        onClose={handleCloseDeleteCompanyDialog}
        onConfirm={deleteCompanyCallback}
        title={t('company.delete_company_title')}
      >
        <Typography>{t('company.delete_company_text')}</Typography>
      </ConfirmationDialog>
    </>
  );
};
export default observer(AdminCompaniesDataGrid);
